import React, { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { GlassTabbar } from '@/components/ui/GlassTabbar';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

const StoreIcon = () => (
  <svg viewBox="0 0 18 20" className="w-[18px] h-5" style={{ fill: 'currentColor' }}>
    <path d="M16.8035 6.96427C16.1335 6.22427 15.1235 5.79427 13.7235 5.64427V4.88427C13.7235 3.51427 13.1435 2.19427 12.1235 1.27427C11.6202 0.812823 11.025 0.462927 10.3771 0.247511C9.72909 0.032095 9.04292 -0.0439787 8.3635 0.0242742C5.9735 0.254274 3.9635 2.56427 3.9635 5.06427V5.64427C2.5635 5.79427 1.5535 6.22427 0.883496 6.96427C-0.0865043 8.04427 -0.0565042 9.48427 0.0534958 10.4843L0.753496 16.0543C0.963496 18.0043 1.7535 20.0043 6.0535 20.0043H11.6335C15.9335 20.0043 16.7235 18.0043 16.9335 16.0643L17.6335 10.4743C17.7435 9.48427 17.7635 8.04427 16.8035 6.96427ZM8.5035 1.41427C8.98813 1.36559 9.47758 1.41913 9.94023 1.57143C10.4029 1.72372 10.8284 1.97138 11.1894 2.29841C11.5503 2.62544 11.8387 3.02456 12.0357 3.46998C12.2328 3.91539 12.3343 4.39721 12.3335 4.88427V5.58427H5.3535V5.06427C5.3535 3.28427 6.8235 1.57427 8.5035 1.41427ZM5.2635 11.1543H5.2535C4.7035 11.1543 4.2535 10.7043 4.2535 10.1543C4.2535 9.60427 4.7035 9.15427 5.2535 9.15427C5.8135 9.15427 6.2635 9.60427 6.2635 10.1543C6.2635 10.7043 5.8135 11.1543 5.2635 11.1543ZM12.2635 11.1543H12.2535C11.7035 11.1543 11.2535 10.7043 11.2535 10.1543C11.2535 9.60427 11.7035 9.15427 12.2535 9.15427C12.8135 9.15427 13.2635 9.60427 13.2635 10.1543C13.2635 10.7043 12.8135 11.1543 12.2635 11.1543Z" />
  </svg>
);

const GavelIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: 'currentColor' }}>
    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM19 12C19.64 12 20.21 12.28 20.6 12.72L22.28 15.65C23.09 17.06 22.14 18.8 20.53 18.8H3.47C1.86 18.8 0.91 17.06 1.72 15.65L3.4 12.72C3.79 12.28 4.36 12 5 12C7.3 12 8.79 9.38 7.39 7.43C6.44 6.11 7.39 4.25 9.02 4.25C10.08 4.25 10.97 4.97 11.23 6H12.77C13.03 4.97 13.92 4.25 15 4.25C16.63 4.25 17.58 6.13 16.61 7.45C15.22 9.39 16.7 12 19 12ZM7.76 16.25L5.76 13.5H5C4.85 13.5 4.7 13.56 4.6 13.67L4.1 14.53C3.92 14.85 4.14 15.25 4.5 15.25H7.76V16.25ZM19.9 14.53L19.4 13.67C19.3 13.56 19.15 13.5 19 13.5H18.24L16.24 16.25V15.25H19.5C19.86 15.25 20.08 14.85 19.9 14.53Z" />
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: 'currentColor' }}>
    <path d="M3 18H21V16H3V18ZM3 13H21V11H3V13ZM3 6V8H21V6H3Z" />
  </svg>
);

const TemplatesIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: 'currentColor' }}>
    <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
  </svg>
);

const CubeIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: 'currentColor' }}>
    <path d="M12 2 3 7v10l9 5 9-5V7l-9-5zm0 2.3 6.5 3.6-6.5 3.6L5.5 7.9 12 4.3zM5 9.4l6 3.3v6.7l-6-3.3V9.4zm8 10V12.7l6-3.3v6.7l-6 3.3z" />
  </svg>
);

interface TemplatePreviewProps {
    onBack: () => void;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({ onBack }) => {
    const { impactOccurred } = useHapticFeedback();
    const [activeId, setActiveId] = useState('store');

    const tabs = [
        { id: 'store', label: 'Store', icon: <StoreIcon /> },
        { id: 'auctions', label: 'Auctions', icon: <GavelIcon /> },
        { id: 'menu', label: 'Menu', icon: <MenuIcon /> },
        { id: 'profile', label: 'Profile', icon: <TemplatesIcon /> },
    ];

    const handleSelect = (id: string) => {
        impactOccurred('light');
        setActiveId(id);
    };

    return (
        <div className="min-h-screen bg-tg-bg text-tg-text font-sans relative flex flex-col">
            {/* Header */}
            <div className="pt-[calc(var(--tg-safe-area-inset-top,0px)+12px)] px-5 flex items-center gap-3">
                <button
                    onClick={() => { impactOccurred('light'); onBack(); }}
                    className="w-10 h-10 rounded-full bg-tg-bg/20 backdrop-blur-md flex items-center justify-center text-tg-text border border-white/10 active:scale-95 transition-transform"
                >
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h1 className="text-lg font-black tracking-tight">Template Preview</h1>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-tg-hint">Liquid Glass · Portals</p>
                </div>
            </div>

            {/* Intro badge */}
            <div className="px-5 mt-6">
                <div className="rounded-[20px] border border-white/10 bg-white/[0.05] backdrop-blur-md px-4 py-3">
                    <p className="text-[11px] leading-relaxed text-tg-hint">
                        Glass bottom-bar تجربة مباشرة — اسحب على الكبسولة وشوف حركة المؤشر (sliding pill) + زجاجية SVG distortion. كبسولة قاعدة سوداء، المؤشر يتحرك باتجاه التمرير مع نبضة scale.
                    </p>
                </div>
            </div>

            {/* Live demo */}
            <div className="flex-1 flex flex-col items-center justify-center px-5">
                <span className="mb-6 text-[10px] font-black uppercase tracking-[0.3em] text-tg-hint">Live Demo</span>

                <div className="w-full max-w-[512px]">
                    <GlassTabbar
                        tabs={tabs}
                        activeId={activeId}
                        onSelect={handleSelect}
                        accent="#3390EC"
                    />
                </div>

                <div className="mt-8 flex gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/[0.05] border border-white/10 backdrop-blur-md flex items-center justify-center" style={{ transform: 'translateZ(0)' }}>
                        <CubeIcon />
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white/[0.05] border border-white/10 backdrop-blur-md flex items-center justify-center">
                        <TemplatesIcon />
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white/[0.05] border border-white/10 backdrop-blur-md flex items-center justify-center">
                        <MenuIcon />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TemplatePreview;